import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { tap, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { GuardModalService } from '@ui/misc/guard-modal';
import { UpsertTagViewModel } from '@apps/cmdb/models';
import { IAuroraState } from '@apps/aurora.state';
import * as fromRootStore from '@apps/root/store';
import * as fromCmdbStore from '@apps/cmdb/store';
import { CmdbConstants, TagColor } from '@apps/cmdb/cmdb.constants';
import { PopinModalRef } from '@ui/misc/popin-modal/popin-modal.ref';
import { AppConstants } from '@apps/app.constants';
import { OpenPopinOriginator } from '@common/enum/open-popin-originator.enum';
import { FormsService } from '@common/services/forms.service';

import { UpsertTagComponent } from '../upsert-tag/upsert-tag.component';
import { forbiddenCharacters } from '@common/validators';

@Component({
    templateUrl: './create-tag.component.html',
    styleUrls: [
        '../upsert-tag/upsert-tag.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateTagComponent extends UpsertTagComponent implements OnInit, OnDestroy
{
    public defaultFgColor = TagColor.FgColor;
    public defaultBgColor = TagColor.BgColor;
    private closeOnFirstClickOutside: boolean = false;

    constructor (
        popinModalRef: PopinModalRef,
        guardModalService: GuardModalService,
        store: Store<IAuroraState>,
        private formsService: FormsService,
        private formBuilder: UntypedFormBuilder,
        private router: Router
    )
    {
        super(guardModalService, store);

        if (popinModalRef.data)
        {
            this.openPopinOriginator = parseInt(popinModalRef.data[AppConstants.OpenPopinOriginator], 10);
            if (this.openPopinOriginator === OpenPopinOriginator.CMDB_TAGS)
            {
                this.closeOnFirstClickOutside = true;
            }
        }
        else
        {
            this.openPopinOriginator = OpenPopinOriginator.NO_ROUTE;
        }
    }

    ngOnInit ()
    {
        this.createForm();
        this.store.dispatch(
            fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: false })
        );
        Promise.resolve().then(() => this.isShown = true);
        this.formGroup.valueChanges
            .pipe(
                tap(() =>
                {
                    this.store.dispatch(
                        fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: true })
                    );
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    onSave = () =>
    {
        this.formsService.markControlsDirty(this.formGroup);
        if (this.formGroup.invalid)
        {
            return;
        }

        const tag: UpsertTagViewModel = {
            categoryName: this.formGroup.get(this.CategoryField).value,
            label: this.formGroup.get(this.LabelField).value,
            bgColor: this.formGroup.get(this.BgColorField).value,
            fgColor: this.formGroup.get(this.FgColorField).value,
            description: this.formGroup.get(this.DescriptionField).value
        };

        this.store.dispatch(
            fromCmdbStore.CmdbTagsActions.CmdbTagCreateRequested({ tag })
        );
        this.store.dispatch(
            fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: false })
        );
        this.close();
    };

    onClickOutside ()
    {
        if (!this.closeOnFirstClickOutside)
        {
            this.closeOnFirstClickOutside = true;
        }
        else
        {
            this.onClosePopin();
        }
    }

    onClose = () =>
    {
        this.close();
    };

    protected close ()
    {
        this.isShown = false;
        this.store.dispatch(
            fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: false })
        );
        this.router.navigate(CmdbConstants.ToTagsRoute());
    }

    private createForm ()
    {
        this.formGroup = this.formBuilder.group({
            [this.CategoryField]: [
                'Global',
                [
                    Validators.required,
                    Validators.maxLength(CmdbConstants.TagMaxLength),
                    forbiddenCharacters(CmdbConstants.TagForbiddenCharacters)
                ]
            ],
            [this.LabelField]: [
                '',
                [
                    Validators.required,
                    Validators.maxLength(CmdbConstants.TagMaxLength),
                    forbiddenCharacters(CmdbConstants.TagForbiddenCharacters)
                ]
            ],
            [this.DescriptionField]: [''],
            [this.FgColorField]: [TagColor.FgColor, [Validators.required]],
            [this.BgColorField]: [TagColor.BgColor, [Validators.required]]
        });
    }
}
